import get from 'lodash/get';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CopyToClipboard from 'react-copy-to-clipboard';
import Container from '@mui/material/Container';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getCollectionDefinitions } from 'api/collection_definitions';
import { getSection, getDynamicTableRow, updateDynamicTableRow } from 'api/sections';
import MKBox from 'components/MaterialKit/MKBox';
import EditAttributesForm from 'components/EditAttributesForm';
import MKTypography from 'components/MaterialKit/MKTypography';
import Button from 'components/Button';
import { handleErrorResponse, parseJSON } from 'utils/general';
import { useAuth } from 'contexts/auth';
import { getLocaleMap } from 'utils/locales';

const EditSectionAttributesPage = () => {
  const [currentSection, setCurrentSection] = useState(null);
  const [sectionConfig, setSectionConfig] = useState({});
  const [collectionDefinitions, setCollectionDefinitions] = useState([]);
  const { sectionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();

  const [lm, setLm] = useState({});

  useEffect(() => {
    getLocaleMap(['copy_id_button'])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const { section_definition, dyn_t } = currentSection || {};
  const collectionDefinition = section_definition?.collection_definition;
  const collectionDefinitionId = collectionDefinition?.collection_definition_id;
  const collectionDefinitionAttributes = collectionDefinition?.attributes;

  const fetchSectionFromApi = useCallback(() => {
    const sectionParams = {
      $expand: 'section_definition/collection_definition/attributes',
    };
    return getSection(sectionId, sectionParams)
      .then(({ data }) => {
        setCurrentSection(data);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [sectionId, setAuth]);

  const fetchSectionDataFromApi = useCallback(() => {
    return ((collectionDefinitionId && dyn_t) ? (
      getDynamicTableRow(collectionDefinitionId, dyn_t, {})
    ) : Promise.resolve({ data: null }))
      .then(({ data }) => {
        const jsonStrValue = get(data, 'json_short_data') || get(data, 'json_big_data');
        const jsonValue = parseJSON(jsonStrValue, {});
        setSectionConfig(jsonValue);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [collectionDefinitionId, dyn_t, setAuth]);

  const fetchCollectionDefinitionsFromApi = useCallback(() => {
    const collectionDefinitionParams = {
      $orderBy: 'name',
      $expand: 'attributes',
    };
    return getCollectionDefinitions(collectionDefinitionParams)
      .then(({ data }) => {
        const filteredData = (data || []).filter(({ attributes }) => {
          return attributes?.length > 0;
        });
        setCollectionDefinitions(filteredData);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [setAuth]);

  const updateSectionDataToApi = useCallback((values) => {
    const updateBody = {
      json_short_data: JSON.stringify(values),
    };
    if (!collectionDefinitionId || !dyn_t) {
      return Promise.resolve();
    }
    return updateDynamicTableRow(collectionDefinitionId, dyn_t, updateBody)
      .then(({ data }) => {
        const jsonStrValue = get(data, 'json_short_data') || get(data, 'json_big_data');
        const jsonValue = parseJSON(jsonStrValue, {});
        setSectionConfig(jsonValue);
        if (searchParams.get('from')) {
          navigate(`${searchParams.get('from')}?mode=author`);
        } else {
          navigate(-1);
        }
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [collectionDefinitionId, dyn_t, searchParams, navigate, setAuth]);

  const onClickCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    fetchSectionFromApi();
  }, [fetchSectionFromApi]);

  useEffect(() => {
    fetchSectionDataFromApi();
  }, [fetchSectionDataFromApi]);

  useEffect(() => {
    fetchCollectionDefinitionsFromApi();
  }, [fetchCollectionDefinitionsFromApi]);

  return (
    <MKBox flex={1} bgColor="light" py="2rem">
      <Container>
        <MKBox display="flex" alignItems="center" mb="2rem">
          <MKTypography variant="h3">
            {collectionDefinition?.name}
          </MKTypography>
          <MKBox ml="1rem">
            <CopyToClipboard
              text={sectionId}
              onCopy={(text, result) => console.log({ text, result })}
            >
              <Button
                circular
                color="secondary"
                variant="gradient"
                size="small"
                fontSize={12}
              >
                <ContentCopyIcon sx={{ mr: '0.4rem' }} />
                {lm.copy_id_button}
              </Button>
            </CopyToClipboard>
          </MKBox>
        </MKBox>
        <EditAttributesForm
          attributes={collectionDefinitionAttributes}
          config={sectionConfig}
          collectionDefinitions={collectionDefinitions}
          onSave={updateSectionDataToApi}
          onCancel={onClickCancel}
        />
      </Container>
    </MKBox>
  );
};

export default EditSectionAttributesPage;
